<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrErrorBanner,
  HrFormDialog,
  HrList,
  HrListItem,
  HrListSectionHeader,
  HrSubtleBtn,
  HrTextField,
  notifyNegative,
} from 'src/design-system';
import { computed, onMounted, ref, watch } from 'vue';

import { useServiceCodes } from '../composables/useServiceCodes';
import { CODE_LIFECYCLE, codeLifecycleOn } from '../presenters/chargeLineRowPresenter';
import MintServiceCodeForm from './MintServiceCodeForm.vue';
import MintServiceCodeReview from './MintServiceCodeReview.vue';

/**
 * Pick a service code to add to the charge master.
 *
 * IT SHOWS EXPIRED AND FUTURE CODES ON PURPOSE, and that is the whole reason this is a dialog with
 * groups rather than a dropdown of what is billable today. A picker filtered to currently-valid
 * codes shows nothing for the code somebody is looking for across a changeover — and a code that
 * cannot be FOUND is a code that gets re-created, which is exactly how a clinic ends up with its own
 * shadow of a code the catalog already governs. Finding it is the point; adding it is secondary.
 *
 * NOTHING IS DISABLED SILENTLY, which is a narrower rule than "nothing is disabled". A code already
 * on the sheet is not selectable — there is nothing for a second selection to do — but it is SHOWN,
 * and it says why. The failure worth avoiding is a dead control with no explanation, not a dead
 * control as such: hiding the code sends the same person looking for it again, and letting them
 * pick it promises an action that does not exist.
 *
 * A CODE MARKED FOR REMOVAL IS SELECTABLE, because there selection does something real — it puts the
 * row back. That is the one case where picking a code already on the sheet changes anything.
 *
 * Either way the picker is not where one-line-per-code is enforced. That invariant lives in the
 * write path, which is the only place that can hold it against a second tab or an import.
 *
 * SEARCH MATCHES CODE OR DESCRIPTOR, because a biller who knows `97153` and a clinician who knows
 * "direct treatment" are both looking for the same row and neither should have to learn the other's
 * vocabulary.
 */
const props = defineProps({
  /** The clinic whose own codes are offered alongside ours, and which may author another. */
  clinicId: {
    type: String,
    required: true,
  },
  /**
   * Codes already on the sheet. Shown with a state hint saying so, and not selectable — there is
   * nothing a second selection could do.
   * @type {string[]}
   */
  onSheetCodeIds: {
    type: Array,
    default: () => ([]),
  },
  /**
   * Codes on the sheet but marked for removal. Selecting one restores its row, which is why they are
   * offered rather than hidden: a marked code still holds its one place on the sheet.
   * @type {string[]}
   */
  pendingRemovalCodeIds: {
    type: Array,
    default: () => ([]),
  },
});

const emit = defineEmits({
  /** Fired with the chosen code's id. */
  ok: null,
  /** Fired when the dialog is dismissed without choosing. */
  hide: null,
});

/**
 * The dialog root. Quasar's `Dialog.create({ component })` drives the component through `show()` and
 * `hide()`, so a component opened that way must expose both — see `defineExpose` below. Without them
 * the plugin rejects it outright and it never renders.
 */
const dialog = ref(null);

const codes = useServiceCodes();
const search = ref('');

/** The search term as a string, whatever the field is holding. See `matches` for why this exists. */
const searchTerm = computed(() => (search.value ?? '').trim());

/**
 * Which of the three things this surface is doing — SEARCHING, DEFINING, or REVIEWING.
 *
 * ONE DIALOG, THREE MODES, RATHER THAN A STACK OF THEM. Finding a code, defining one and vouching
 * for it are three steps of one errand: the same person, the same question, one step further on
 * each time. A modal stacked over a modal would say they are separate tasks, bury what came before
 * it, and give the reader two layers of chrome to dismiss. Swapping the body keeps the search
 * intact underneath, which is what makes going back free rather than a re-entry.
 *
 * THE REVIEW STEP IS THE TICKET'S, and it is here now because the objection to it expired. It was
 * left out on the grounds that no design-system component offers a multi-step form dialog and that
 * building a stepper inside a feature would be a new pattern arriving without review. Once this
 * surface grew its own mode switch — for reasons that had nothing to do with the review — a third
 * mode costs no new pattern at all, so the reason not to build it was gone.
 */
const MODE = { SEARCH: 'search', DEFINE: 'define', REVIEW: 'review' };
const mode = ref(MODE.SEARCH);

/** The draft the form reports: present when it can be written, null when it cannot. */
const mintDraft = ref({ draft: null, code: '' });
const isConfirmed = ref(false);

/**
 * Whether Review has been pressed against the current draft.
 *
 * THE UNIT BASIS IS THE ONE FIELD THAT CANNOT SPEAK FOR ITSELF. A segmented button is not a
 * `q-field`, so `HrForm` never validates it and `lazy-rules` never reaches it — every other control
 * reports itself the moment it is left, and this one has to be told the primary action was tried.
 * Until it was, Review appeared to do nothing at all on a form with no basis chosen.
 *
 * Cleared on the way back to the search, so the next code starts quiet rather than opening with an
 * error against fields nobody has filled in yet.
 */
const hasTriedReview = ref(false);
const isMinting = ref(false);

/**
 * A FAILED READ IS SAID ON SCREEN, because every other signal this dialog has looks like success. The
 * groups render off what loaded and the no-matches line waits for `hasLoaded`, so a rejected read
 * leaves a search field above nothing at all — indistinguishable from a catalog with no codes in
 * it, and an invitation to go and add a code we already carry. Same treatment as the treatment-service
 * editor, which reads the same catalog.
 */
const loadFailed = ref(false);

onMounted(() => codes.load({ clinicId: props.clinicId }).catch((error) => {
  loadFailed.value = true;
  console.error('Failed to load the service codes', error);
}));

function hide() {
  dialog.value?.hide();
}

/**
 * Picking closes the dialog and hands the code back to the host, which stages the row.
 *
 * IT REPORTS WHETHER IT MINTED, because only this dialog knows. The host used to derive it — a
 * custom code absent from its copy a moment ago must have just been created — and that derivation
 * reads a copy that can be stale or missing. When the host's re-read after a mint failed, the new
 * code looked like no code of ours at all, so it was never tracked as this session's: the cleanup
 * on close skipped it and its row offered neither retire nor delete, leaving a live record nobody
 * could remove. The mint has already succeeded by this point, so what follows must not depend on a
 * later read succeeding.
 */
function pick(serviceCodeId, { isMinted = false } = {}) {
  emit('ok', { serviceCodeId, isMinted });
  hide();
}

/**
 * Switching to the define mode, carrying the search term with it.
 *
 * THE TYPED TERM PREFILLS THE CODE. Somebody who searched `200` and did not find it has already told
 * us what they are after; asking them to type it again is the surface making them repeat themselves
 * to prove they meant it.
 */
const defineOwnCode = () => {
  mode.value = MODE.DEFINE;
};

/**
 * Forward from the form to the review, once there is something worth reviewing.
 *
 * IT RUNS AFTER THE FORM'S OWN VALIDATION, not instead of it. `HrForm` collects the fields' `:rules`
 * and only emits `submit` when they pass, so by the time this is called the code, the description
 * and the date have already reported themselves — each beside its own control, which is where an
 * error belongs. There is nothing here re-deriving what a field already knows.
 *
 * WHAT IT STILL HAS TO CHECK is the duplicate, because that is a fact about the clinic's other
 * codes rather than about any one field — the banner beside the code is where it is said, since it
 * carries the way out. Every actual field reports itself.
 */
const reviewCode = () => {
  hasTriedReview.value = true;

  if (mintDraft.value.draft === null) {
    return;
  }
  mode.value = MODE.REVIEW;
};

/** Back from the review to the fields. The draft is untouched — the form stays mounted. */
const backToForm = () => {
  mode.value = MODE.DEFINE;
};

/**
 * Back to the results, with the query still in the field.
 *
 * NOTHING IS DISCARDED ON THE WAY OUT, because nothing was written — the form is remounted on the
 * next visit, which is the honest behavior for an abandoned draft, and the SEARCH is what the user
 * is returning to. Coming back to an empty search box would be the same insult as an empty code
 * field, one step later.
 */
const backToSearch = () => {
  mode.value = MODE.SEARCH;
  hasTriedReview.value = false;
};

/** Advisory: managed codes carrying the string being typed. Never blocks. */
const managedMatches = computed(() => codes.managedCodesMatching(mintDraft.value.code));

/**
 * THE AFFIRMATION DOES NOT SURVIVE AN EDIT, and this watcher is the whole of that rule.
 *
 * It is set on the review step and was previously never cleared: tick the box, press Back, change
 * the code or the unit basis or the start date, press Review again, and the box is still ticked with
 * Add code already live. The gate would then cover values nobody reviewed — and everything it guards
 * is immutable once written, so there is no correcting it afterwards.
 *
 * Watching the DRAFT rather than each field, because the draft is exactly what the review restates.
 */
watch(() => mintDraft.value.draft, () => {
  isConfirmed.value = false;
});

/** Context: every code of the clinic's own under that string, revisions and mistakes alike. */
const ownMatches = computed(() => codes.ownCodesMatching(mintDraft.value.code));

/**
 * Blocking: the same code already starting on the same day, which the document id cannot represent
 * twice. Narrower than `ownMatches` on purpose — a second PERIOD is legitimate and is not refused.
 */
const ownMatch = computed(() => codes.ownCodeOn(mintDraft.value.code, mintDraft.value.validFrom));

/**
 * Writing the code, then handing it back exactly as a pick does.
 *
 * THIS ONE MAY BE DISABLED, unlike the step before it, and the two are not inconsistent. On the form
 * the outstanding thing is a field somewhere up the page, so a dead button hides the answer. Here
 * there is exactly one requirement, it is the only control on screen, and it sits directly above the
 * button — so a disabled action points straight at what unlocks it.
 */
const addCode = async () => {
  // `isMinting` IS PART OF THE GUARD. Without it a second submit while the first write is in flight
  // starts a second mint, which reads the document the first one just wrote and reports
  // `ServiceCodeAlreadyExists` — telling the user they have a duplicate of a code they successfully
  // created. The button's own loading state hides the click but not a held Enter.
  if (mintDraft.value.draft === null || !isConfirmed.value || isMinting.value) {
    return;
  }
  isMinting.value = true;
  try {
    const serviceCodeId = await codes.mint({
      clinicId: props.clinicId,
      ...mintDraft.value.draft,
    });

    pick(serviceCodeId, { isMinted: true });
  } catch (error) {
    // THE FAILURES WE EXPECT GET THEIR OWN WORDING; anything else is unexpected and never shown raw.
    // The surface stays open in every case — each of these is something to correct here, not a
    // reason to send somebody back to a list they already searched.
    notifyNegative(i18n.t(`settings.billing.chargeMaster.mint.${{
      ServiceCodeAlreadyExists: 'notifyDuplicate',
      CodeStringRejected: 'notifyUnstorable',
      TypeError: 'notifyIncomplete',
    }[error?.name] ?? 'notifyFailed'}`));
    console.error('Failed to mint an org-authored service code', error);
  } finally {
    isMinting.value = false;
  }
};

defineExpose({
  show: () => dialog.value?.show(),
  hide,
});

const matches = (serviceCode) => {
  // `clearable` WRITES NULL, NOT AN EMPTY STRING. Reading `.trim()` off it threw on the clear, and a
  // render that throws takes the whole dialog body with it — the search field stayed, everything
  // under it vanished, and nothing said why. Every reader of this ref coalesces for that reason.
  const needle = searchTerm.value.toUpperCase();

  if (needle.length === 0) {
    return true;
  }
  return `${serviceCode.code ?? ''}`.toUpperCase().includes(needle)
    || `${serviceCode.descriptor ?? ''}`.toUpperCase().includes(needle);
};

/**
 * Three groups, in the order a person scans them: what they can bill now, what is on its way out,
 * and what starts later. Tombstoned codes are excluded — they were never real codes, so offering one
 * would put a charge against something that should not exist.
 */
const groups = computed(() => {
  const found = codes.serviceCodes.value
    .filter((serviceCode) => serviceCode.isDeleted !== true && matches(serviceCode))
    .sort((a, b) => `${a.code}`.localeCompare(`${b.code}`));

  const inGroup = (...lifecycles) => found.filter(
    (serviceCode) => lifecycles.includes(codeLifecycleOn(serviceCode, codes.asOf)),
  );

  return [
    { key: 'active', codes: inGroup(CODE_LIFECYCLE.ACTIVE) },
    { key: 'ending', codes: inGroup(CODE_LIFECYCLE.EXPIRING, CODE_LIFECYCLE.EXPIRED) },
    { key: 'upcoming', codes: inGroup(CODE_LIFECYCLE.FUTURE) },
  ].filter((group) => group.codes.length > 0);
});

/**
 * What a row says about a code that already has a home — and nothing at all for one that does not.
 *
 * The hint is what keeps a disabled row from being a dead end: it says WHY the code cannot be picked,
 * so the answer is on screen next to the control rather than somewhere off it.
 */
const isPendingRemoval = (serviceCodeId) => props.pendingRemovalCodeIds.includes(serviceCodeId);

/**
 * Selectable unless the code is already on the sheet and staying there.
 *
 * A marked code stays selectable because picking it RESTORES the row — a real outcome. A code
 * already present has no second thing to do, so offering the click would promise one.
 */
const isSelectable = (serviceCodeId) => isPendingRemoval(serviceCodeId)
  || !props.onSheetCodeIds.includes(serviceCodeId);

const stateHintFor = (serviceCodeId) => {
  if (isPendingRemoval(serviceCodeId)) {
    return i18n.t('settings.billing.chargeMaster.picker.captionPendingRemoval');
  }
  return props.onSheetCodeIds.includes(serviceCodeId)
    ? i18n.t('settings.billing.chargeMaster.picker.captionOnSheet')
    : null;
};

/** Every matching code, flattened across the groups the list renders. */
const matchingCodes = computed(() => groups.value.flatMap((group) => group.codes));

/**
 * The one code the search has narrowed to, or null.
 *
 * ENTER ACTS ONLY WHEN THERE IS NO CHOICE LEFT TO MAKE, which is the whole design of this. Nothing
 * is highlighted and no arrow keys move a selection: highlighting the first of several would promise
 * a combobox — `aria-activedescendant`, arrow-key navigation, focus held in the input — and half of
 * that contract is worse than none of it, because a keyboard user gets an affordance that does not
 * respond and a screen-reader user gets an Enter key that does something unannounced.
 *
 * Narrowed to exactly one, there is no ambiguity to communicate and no active option to track. It
 * covers the case that matters — somebody who knows `97153` types it and goes without leaving the
 * field — and claims nothing else.
 *
 * A SOLE MATCH THAT IS NOT SELECTABLE DOES NOT COUNT. A code already on the sheet has nothing for
 * Enter to do, and acting on it would be a keystroke that silently does nothing.
 */
const soleMatch = computed(() => {
  if (matchingCodes.value.length !== 1) {
    return null;
  }
  const [only] = matchingCodes.value;

  return isSelectable(only.id) ? only : null;
});

/**
 * The search field's hint, which is also how the Enter shortcut is discovered.
 *
 * Without a highlight there is nothing on screen to suggest Enter does anything, so the hint says so
 * at the moment it becomes true. An affordance nobody knows about is half wasted.
 */
const searchHint = computed(() => (soleMatch.value === null
  ? i18n.t('settings.billing.chargeMaster.picker.hintSearch')
  : i18n.t('settings.billing.chargeMaster.picker.hintEnterToAdd', { code: soleMatch.value.code })));

/**
 * ON KEY-UP, NOT KEY-DOWN, and the difference is a real bug rather than a style choice.
 *
 * Selecting on keydown closes this dialog while the key is still held. Focus then returns to the
 * "Add code" button that opened it — correctly — and the key-UP lands on that button and re-fires
 * it, reopening the picker the instant it closed. Acting on key-up instead means the last event of
 * the keystroke is the one we consume, so nothing is left to reach the opener.
 *
 * The keydown is still intercepted, with `.prevent` and no handler, so Enter cannot submit the form
 * dialog around us.
 *
 * `HrFormDialog` offers `noRefocus` for this same collision, but it fixes it by not returning focus
 * at all — which costs a keyboard user their place. Consuming the event is the cheaper trade.
 */
const onSearchEnter = () => {
  if (soleMatch.value !== null) {
    pick(soleMatch.value.id);
  }
};

const hasNoMatches = computed(() => codes.hasLoaded.value && groups.value.length === 0);
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    :title="i18n.t({
      [MODE.DEFINE]: 'settings.billing.chargeMaster.mint.title',
      [MODE.REVIEW]: 'settings.billing.chargeMaster.mint.titleReview',
    }[mode] ?? 'settings.billing.chargeMaster.picker.title')"
    size="md"
    data-e2e="dialog-service-code-picker"
    @submit="mode === MODE.DEFINE ? reviewCode() : addCode()"
    @hide="emit('hide')"
  >
    <!--
      THE SEARCH STAYS MOUNTED UNDER `v-show`, not `v-if`, and that is what makes Back free. Unmounting
      it would drop the query and the scroll position, so returning from the form would land somebody
      at the top of an empty search — the same insult as an empty code field, one step later.
    -->
    <div v-show="mode === MODE.SEARCH">
      <!--
        FOCUSED ON OPEN. Finding a code is the only reason this dialog exists, and the catalog is
        long enough that scrolling it is the slow path — someone who knows the code should be able to
        type it without first reaching for the field. It also puts the keyboard user at the one
        control that matters instead of at the top of a long list.
      -->
      <hr-text-field
        v-model="search"
        :label="i18n.t('settings.billing.chargeMaster.picker.inputSearch')"
        :hint="searchHint"
        clearable
        autofocus
        data-e2e="input-search-service-code"
        @keydown.enter.prevent
        @keyup.enter="onSearchEnter"
      />

      <hr-error-banner
        v-if="loadFailed"
        class="q-mt-md"
        :message="i18n.t('settings.billing.chargeMaster.picker.textLoadFailed')"
        data-e2e="banner-picker-load-failed"
      />

      <hr-list
        v-for="group in groups"
        :key="group.key"
        class="q-mt-md"
      >
        <hr-list-section-header>
          {{ i18n.t(`settings.billing.chargeMaster.picker.group.${group.key}`, { count: group.codes.length }) }}
        </hr-list-section-header>

        <hr-list-item
          v-for="serviceCode in group.codes"
          :key="serviceCode.id"
          :label="`${serviceCode.code} — ${serviceCode.descriptor}`"
          :caption="stateHintFor(serviceCode.id)"
          :clickable="isSelectable(serviceCode.id)"
          :disable="!isSelectable(serviceCode.id)"
          :data-e2e="`item-service-code-${serviceCode.code}`"
          @click="pick(serviceCode.id)"
        />
      </hr-list>

      <!--
        ONE THOUGHT, ONE NEXT STEP. Nothing matched, so the message names what was searched for and
        offers the only thing left to do — rather than splitting "no matches", a section heading and
        an action across three blocks that each say a third of the same sentence.
      -->
      <div
        v-if="hasNoMatches"
        class="q-mt-md"
        data-e2e="empty-service-code-search"
      >
        <p>{{ i18n.t('settings.billing.chargeMaster.picker.textNoMatches', { search: searchTerm }) }}</p>
        <hr-confirm-btn
          :label="i18n.t('settings.billing.chargeMaster.picker.buttonDefineOwnCode')"
          data-e2e="button-define-own-code"
          @click="defineOwnCode"
        />
      </div>

      <!--
        WITH RESULTS ON SCREEN it is a trailing list item instead, under its own divider — the honest
        next step for somebody who has scanned the matches and still not found theirs. Never an action
        in the footer, which would offer minting as a peer of searching to a person who has not
        searched yet, and that is how a clinic ends up shadowing a code the catalog already governs.
      -->
      <hr-list
        v-else-if="!loadFailed"
        class="q-mt-md"
      >
        <hr-list-section-header>
          {{ i18n.t('settings.billing.chargeMaster.picker.groupNotInCatalog') }}
        </hr-list-section-header>

        <hr-list-item
          :label="i18n.t('settings.billing.chargeMaster.picker.itemDefineOwnCode')"
          :caption="i18n.t('settings.billing.chargeMaster.picker.captionDefineOwnCode')"
          clickable
          data-e2e="item-define-own-code"
          @click="defineOwnCode"
        />
      </hr-list>
    </div>

    <!--
      MOUNTED FOR BOTH THE FORM AND THE REVIEW, hidden for the latter rather than torn down. The
      review is a look at this draft, and unmounting the fields to show it would throw away
      everything Back is supposed to return to.
    -->
    <mint-service-code-form
      v-if="mode !== MODE.SEARCH"
      v-show="mode === MODE.DEFINE"
      :initial-code="searchTerm"
      :managed-matches="managedMatches"
      :own-match="ownMatch"
      :own-matches="ownMatches"
      :submit-attempted="hasTriedReview"
      @update:draft="mintDraft = $event"
      @use-managed="pick"
      @use-own="pick"
    />

    <mint-service-code-review
      v-if="mode === MODE.REVIEW && mintDraft.draft !== null"
      v-model:confirmed="isConfirmed"
      :draft="mintDraft.draft"
    />

    <template #actions>
      <!--
        BACK, NOT CANCEL, and the distinction is what the one-surface arrangement buys. Cancel would
        throw away the search as well as the draft; this returns to the results the user was looking
        at, query intact. The × still closes the whole thing, which is the real cancel.
      -->
      <hr-subtle-btn
        v-if="mode === MODE.DEFINE"
        :label="i18n.t('settings.billing.chargeMaster.mint.buttonBackToSearch')"
        data-e2e="button-back-to-search"
        @click="backToSearch"
      />
      <hr-confirm-btn
        v-if="mode === MODE.DEFINE"
        :label="i18n.t('settings.billing.chargeMaster.mint.buttonReview')"
        type="submit"
        data-e2e="button-review-mint-code"
      />

      <hr-subtle-btn
        v-if="mode === MODE.REVIEW"
        :label="i18n.t('settings.billing.chargeMaster.mint.buttonBack')"
        data-e2e="button-back-to-form"
        @click="backToForm"
      />
      <hr-confirm-btn
        v-if="mode === MODE.REVIEW"
        :label="i18n.t('settings.billing.chargeMaster.mint.buttonAdd')"
        type="submit"
        :disable="!isConfirmed"
        :loading="isMinting"
        data-e2e="button-submit-mint-code"
      />
      <!--
        NOT "Done". Picking a code closes this on its own, so the only thing this button ever does is
        leave without choosing — which is what it should say.
      -->
      <hr-dismiss-btn
        v-if="mode === MODE.SEARCH"
        :label="i18n.t('common.buttonCancel')"
        data-e2e="button-cancel-service-code-picker"
        @click="hide"
      />
    </template>
  </hr-form-dialog>
</template>
